import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Drawer } from "@app/components/Drawer";
import { FilterChip } from "@app/components/SortDrawer/FilterChip";
import { Accordion } from "@app/components/Drawer/Accordion";

export const EVENT_SORT_DRAWER_OPEN = "sortDrawer:open";
export const EVENT_SORT_DRAWER_CLOSE = "sortDrawer:close";

export interface Filter {
  id: string;
  name: string;
  options: {
    id: string;
    name: string;
    value: string;
    resultsLength?: number;
  }[];
}

export interface ActiveFilters {
  id: string;
  options: string[];
}

interface SortDrawerProps {
  totalResults?: number;
  filters: Filter[];
  activeFilters: ActiveFilters[];
  onFilterUpdate?: (activeFilters: ActiveFilters[]) => void;
  onClear?: () => void;
}

export const SortDrawer = ({ totalResults, filters, activeFilters, onFilterUpdate, onClear }: SortDrawerProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    document.body.classList.add("no-scroll");
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    document.body.classList.remove("no-scroll");
  }, []);

  useEffect(() => {
    const drawerOpenCallback = (e: any) => {
      const openAccordionItem = e.detail?.accordionItem;
      if (openAccordionItem) {
        const accordionItem = ref.current?.querySelector(`[data-id="accordion-item-${openAccordionItem}"]`);
        if (!accordionItem?.classList.contains("is-open")) {
          accordionItem?.querySelector(".accordion-item-header")?.click();
        }
      }

      handleOpen();
    };

    const drawerCloseCallback = () => {
      handleClose();
    };

    document.addEventListener(EVENT_SORT_DRAWER_OPEN, drawerOpenCallback);
    document.addEventListener(EVENT_SORT_DRAWER_CLOSE, drawerCloseCallback);

    return () => {
      document.removeEventListener(EVENT_SORT_DRAWER_OPEN, drawerOpenCallback);
      document.removeEventListener(EVENT_SORT_DRAWER_CLOSE, drawerCloseCallback);
    };
  }, []);

  const flattenedActiveFilters = useMemo(() => {
    return activeFilters.flatMap((filter) =>
      filter.options.map((option) => ({
        id: filter.id,
        value: option,
      })),
    );
  }, [activeFilters]);

  const accordionItems = useMemo(
    () => [
      {
        title: "Sort by",
        content: (
          <>
            <label className="radio-item" for="default">
              <input className="radio-input" type="radio" id="default" name="sort" value="default" checked={true} />
              <div>Default</div>
            </label>
          </>
        ),
      },
      ...filters.map((filter) => ({
        id: filter.id,
        title: filter.name,
        content: (
          <>
            {filter.options.map((option) => {
              return (
                <label className="checkbox-item" htmlFor={option.id}>
                  <input
                    className="checkbox-input"
                    type="checkbox"
                    id={option.id}
                    name={option.id}
                    value={option.id}
                    checked={
                      !!activeFilters.find(
                        (activeFilter) => activeFilter.id === filter.id && activeFilter.options.includes(option.id),
                      )
                    }
                    onChange={(e) => {
                      const activeFilterIndex = activeFilters.findIndex((f) => f.id === filter.id);
                      if (activeFilterIndex === -1) return;

                      const activeFilter = activeFilters[activeFilterIndex];

                      if (e.target.checked) {
                        if (!activeFilter.options.find((o) => o === option.id)) {
                          const newOptions = [...activeFilter.options, option.id];
                          activeFilters[activeFilterIndex] = { id: filter.id, options: newOptions };
                          onFilterUpdate?.(activeFilters);
                        }
                      } else {
                        if (activeFilter.options.find((o) => o === option.id)) {
                          const newOptions = activeFilter.options.filter((o) => o !== option.id);
                          activeFilters[activeFilterIndex] = { id: filter.id, options: newOptions };
                          onFilterUpdate?.(activeFilters);
                        }
                      }
                    }}
                  />
                  <div className="checkbox-label">
                    <div className="checkbox-label-main">{option.name}</div>
                    {option.resultsLength ? <div className="checkbox-label-sub">{option.resultsLength}</div> : null}
                  </div>
                </label>
              );
            })}
          </>
        ),
      })),
    ],
    [filters, activeFilters],
  );

  return (
    <Drawer position="left" isOpen={isOpen} onClose={handleClose}>
      <div ref={ref} className="h-full flex flex-col">
        <div className="flex gap-4 items-center justify-between py-2 px-4 pr-1.5">
          <div className="text-lg text-brand-bistre">Filter & Sort</div>
          <button
            className="w-[44px] h-[44px] flex items-center justify-center"
            dangerouslySetInnerHTML={{ __html: window.iconCloseMd }}
            onClick={handleClose}
          />
        </div>

        {flattenedActiveFilters.length > 0 && (
          <div className="px-4 pb-4 flex flex-wrap gap-2">
            {flattenedActiveFilters.map((filter) => (
              <FilterChip
                key={filter.value}
                label={filter.value}
                onClose={() => {
                  const f = activeFilters.find((activeFilter) => activeFilter.id === filter.id);
                  if (f) {
                    const newOptions = f.options.filter((option) => option !== filter.value);
                    const newActiveFilters = activeFilters.filter((activeFilter) => activeFilter.id !== filter.id);
                    newActiveFilters.push({ id: filter.id, options: newOptions });
                    onFilterUpdate?.(newActiveFilters);
                  }
                }}
              />
            ))}
            <button className="button button--small button--text button--black" onClick={onClear}>
              <span className="button-text">Clear All</span>
            </button>
          </div>
        )}

        <div className="flex-1 overflow-auto border-t border-b-neutrals-lightGrey">
          <Accordion items={accordionItems} />
        </div>

        <div className="p-4 flex gap-2 bg-neutrals-lightGrey">
          <button
            className="button button--medium button--solid button--white"
            onClick={() => {
              onClear?.();
              setTimeout(() => handleClose(), 100);
            }}
          >
            <span className="button-text">Clear</span>
          </button>
          <button className="flex-1 button button--medium button--solid button--black" onClick={handleClose}>
            <span className="button-text">View {totalResults || ""} Results</span>
          </button>
        </div>
      </div>
    </Drawer>
  );
};
